<template>
  <!-- Recipient journey R7.11: her first send is the password moment, the
       only one in the journey. Asked in the same step as the send; "Not now"
       sends anyway. A guest place has no address to sign in with, so she
       gives her email first; one link goes to it (the "Keep this" mail) and
       the password works once she taps it. -->
  <form class="spw" data-testid="send-password" @submit.prevent="save">
    <p class="spw-ask" data-testid="send-password-ask">{{ PASSWORD_ASK }}</p>
    <label v-if="askEmail" class="spw-field">
      <span>Your email</span>
      <input v-model="email" class="spw-input" type="email" inputmode="email" autocomplete="email"
             data-testid="send-password-email" placeholder="you@example.com" />
      <em>{{ PASSWORD_EMAIL_HELP }}</em>
    </label>
    <label class="spw-field">
      <span>A password</span>
      <input v-model="password" class="spw-input" type="password" autocomplete="new-password"
             data-testid="send-password-input" placeholder="At least 8 characters" />
    </label>
    <p v-if="error" class="spw-err" role="alert">{{ error }}</p>
    <button type="submit" class="spw-save" data-testid="send-password-save" :disabled="busy || !ready">
      {{ busy ? 'Saving…' : `Save and ${actionLabel}` }}
    </button>
    <button type="button" class="spw-notnow" data-testid="send-password-not-now" :disabled="busy" @click="notNow">
      {{ PASSWORD_NOT_NOW }}
    </button>
  </form>
</template>

<script setup>
import { computed, ref } from 'vue'
import { carePassword } from '@/api-service'
import { store } from '@/store'
import { track } from '@/utils/analytics'
import { PASSWORD_ASK, PASSWORD_EMAIL_HELP, PASSWORD_NOT_NOW } from '@/utils/stage-four'

const props = defineProps({
  askEmail: { type: Boolean, default: false },
  // What happens after: "send" or "copy the link".
  actionLabel: { type: String, default: 'send' },
  sessionId: { type: String, default: '' },
})
const emit = defineEmits(['done'])

const email = ref('')
const password = ref('')
const busy = ref(false)
const error = ref('')
const ready = computed(() => password.value.length > 0 && (!props.askEmail || /@/.test(email.value)))

async function save() {
  if (busy.value || !ready.value) return
  busy.value = true
  error.value = ''
  try {
    const r = await carePassword(props.sessionId || store.session_id,
      { password: password.value, email: props.askEmail ? email.value.trim() : '' })
    password.value = ''
    emit('done', { outcome: r && r.pending ? 'pending' : 'saved', message: (r && r.message) || '' })
  } catch (e) {
    const said = e && e.response && e.response.data && e.response.data.error
    error.value = (typeof said === 'string' && said) || 'That did not save. Try again, or tap Not now.'
  } finally {
    busy.value = false
  }
}

function notNow() {
  track('gift_password', { step: 'not_now' })
  password.value = ''
  emit('done', { outcome: 'skipped', message: '' })
}
</script>

<style scoped>
/* Sits inside the send screen (SharePopup's light card): its type and colours. */
.spw { display: flex; flex-direction: column; gap: 12px; margin: 18px 0 4px; padding: 16px 14px; border-radius: 14px;
  background: #f6effd; border: 1px solid #e3d4f5; }
.spw-ask { margin: 0; text-align: center; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 19px; line-height: 1.35;
  color: #2a1845; }
.spw-field { display: flex; flex-direction: column; gap: 6px; font-family: 'Cinzel', serif; font-size: 10.5px;
  letter-spacing: 0.14em; text-transform: uppercase; color: #5a4a72; }
.spw-field em { font-style: normal; text-transform: none; letter-spacing: 0; font-family: Georgia, serif; font-size: 12px;
  color: #6b5a82; }
.spw-input { width: 100%; box-sizing: border-box; min-height: 44px; padding: 10px 12px; border-radius: 10px;
  border: 1px solid #d9c6f2; background: #fbf9f5; color: #2a1845; font-family: Georgia, serif; font-size: 16px;
  text-transform: none; letter-spacing: 0; }
.spw-err { margin: 0; color: #a02a3c; font-size: 14px; text-align: center; }
.spw-save { display: flex; align-items: center; justify-content: center; min-height: 48px; padding: 13px 22px;
  border-radius: 999px; border: 1px solid rgba(240,217,163,0.55); background: linear-gradient(180deg, #7b4bb0, #4e2776);
  color: #FFDFA3; font-weight: 700; font-size: 13px; letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer; }
.spw-save:disabled { opacity: 0.55; cursor: default; }
.spw-notnow { min-height: 44px; background: none; border: none; color: #4e2776; text-decoration: underline;
  text-underline-offset: 3px; font-size: 14px; cursor: pointer; }
</style>
